import { z } from "zod";

import { ResponseError } from "@/api-clients/fetch-config";
import { FAILED_NETWORK_HTTP_STATUS } from "@/data/response-utils";

/**
 * Extracts user-friendly messages from any error type (including ResponseError)
 * Prioritizes server error messages over fallback messages
 */
const errorMessageSchema = z.union([
  z
    .object({ error: z.object({ message: z.string() }) })
    .transform((value) => value.error.message),
  z.object({ error: z.string() }).transform((value) => value.error),
  z.object({ message: z.string() }).transform((value) => value.message),
]);

function getErrorMessage(value: unknown): string | undefined {
  const result = errorMessageSchema.safeParse(value);
  return result.success ? result.data : undefined;
}

export function buildErrorMessage(
  error: unknown,
  fallbackMessage: string = "Something went wrong, please try again.",
): string {
  if (error instanceof ResponseError) {
    if (error.status === FAILED_NETWORK_HTTP_STATUS) {
      return "Unable to connect to the server. Please check your internet connection and try again.";
    }
    return (
      getErrorMessage(error.data) ??
      getStatusCodeMessage(error.status, fallbackMessage)
    );
  }
  return getErrorMessage(error) ?? fallbackMessage;
}

export function getStatusCodeMessage(
  status: number,
  fallbackMessage = "An unknown error occurred. Please try again.",
): string {
  switch (status) {
    case 400:
      return "The information provided is invalid. Please check and try again.";
    case 401:
      return "Your session has expired. Please sign in again.";
    case 403:
      return "You don't have permission to perform this action.";
    case 404:
      return "The requested resource was not found.";
    case 409:
      return "This action conflicts with existing data.";
    case 429:
      return "Too many requests. Please wait a moment and try again.";
    case 500:
    case 502:
    case 503:
    case 504:
      return "We're experiencing technical difficulties. Please try again in a few moments.";
    default:
      return fallbackMessage;
  }
}
